import { toRaw } from 'vue';
import { HaspTemplatePage } from '../Hasp/HaspPage';
import { serializeCommandAction } from '../actions/commandSerializer.js';
import { getTemplateObjectRecords } from './designerObjectIds.js';

/**
 * Compiles a Vue Flow graph (nodes + edges) into an array of OpenHASP .cmd lines.
 * Execution order follows the sequence edges from the trigger node outward.
 * @param {Array} nodes
 * @param {Array} edges
 * @param {object|null} layer - HaspObjectLayer; required to resolve templateRef nodes
 */
export function compileFlow(nodes, edges, layer = null) {
    if (!nodes || nodes.length === 0) return [];

    // Build adjacency: nodeId -> next nodeId via sequence edge
    const nextMap = {};
    for (const edge of edges) {
        nextMap[edge.source] = edge.target;
    }

    // Find the trigger node (no incoming sequence edge)
    const targetIds = new Set(edges.map(e => e.target));
    const triggerNode = nodes.find(n => n.type === 'trigger' && !targetIds.has(n.id));
    if (!triggerNode) return [];

    const lines = [];
    let current = triggerNode.id;
    const visited = new Set();

    while (current && !visited.has(current)) {
        visited.add(current);
        const node = nodes.find(n => n.id === current);
        if (!node) break;

        const nodeLines = nodeToCommands(node, layer);
        lines.push(...nodeLines);

        current = nextMap[current];
    }

    return lines;
}

/** Returns an array of .cmd lines for a single node (usually 1, but templateRef can be many). */
function nodeToCommands(node, layer) {
    const d = node.data ?? {};

    if (node.type === 'templateRef') {
        return compileTemplateRef(d.templatePageId, d.templateObjectId, layer);
    }

    const line = nodeToSingleCommand(node);
    return line ? [line] : [];
}

function compileTemplateRef(templatePageId, templateObjectId, layer) {
    if (!layer || templatePageId === undefined || templatePageId === '') return [];

    // Strip Vue reactive proxy so Konva instanceof checks and internal
    // getChildren() calls work on the real objects, not proxied ones.
    const rawLayer = toRaw(layer);

    const templatePage = rawLayer.getChildren().find(
        c => toRaw(c) instanceof HaspTemplatePage && toRaw(c).attrs.haspid === templatePageId
    );
    if (!templatePage) return [`# templateRef: page ${templatePageId} not found`];

    return getTemplateObjectRecords(toRaw(templatePage), templateObjectId)
        .map(record => `jsonl ${JSON.stringify(record)}`);
}

function nodeToSingleCommand(node) {
    const d = node.data ?? {};

    switch (node.type) {
        case 'trigger':
            return null; // entry point only, no command

        case 'gotoPage': {
            const target = d.pageTarget ?? '1';
            return `page ${target}`;
        }

        case 'clearPage': {
            const page = d.page ?? 'all';
            return `clearpage ${page}`;
        }

        case 'setProperty': {
            if (!d.pageId || d.objectId === undefined || !d.property) return null;
            const value = d.value ?? '';
            return `p${d.pageId}b${d.objectId}.${d.property}=${value}`;
        }

        case 'backlight': {
            const state = d.state !== false ? 1 : 0;
            const brightness = Math.min(255, Math.max(1, parseInt(d.brightness) || 128));
            return `backlight {"state":${state},"brightness":${brightness}}`;
        }

        case 'moodlight': {
            const state = d.state !== false ? 1 : 0;
            const r = parseInt(d.r) || 0;
            const g = parseInt(d.g) || 0;
            const b = parseInt(d.b) || 0;
            return `moodlight {"state":${state},"r":${r},"g":${g},"b":${b}}`;
        }

        case 'output': {
            if (d.outputPin === undefined) return null;
            const state = d.state !== false ? 1 : 0;
            return `output${d.outputPin} ${state}`;
        }

        case 'delay': {
            const ms = parseInt(d.duration) || 500;
            return `delay ${ms}`;
        }

        case 'runScript': {
            const file = (d.filename ?? '').trim();
            if (!file) return null;
            return `run ${file.startsWith('/') ? file : '/' + file}`;
        }

        case 'reboot':
            return `reboot`;

        case 'screenshot':
            return `screenshot`;

        case 'idle': {
            const state = d.idleState ?? 'off';
            return `idle ${state}`;
        }

        case 'jsonl': {
            const payload = (d.payload ?? '').trim();
            if (!payload) return null;
            return `jsonl ${payload}`;
        }

        case 'command':
            return serializeCommandAction({ type: 'command', ...d });

        default:
            return null;
    }
}
